/**
 * PlayerTracking.jsx — Admin Player Tracking widget (#31).
 *
 * Modernized with ThemedTable:
 *   - Drag-to-reorder columns with persistent state
 *   - Sortable Sales, Gold, Player, and Days Silent headers
 *   - Interactive UserCell chips
 *   - Guild dues status badges with stained-glass hover rows
 */
import { Show, For, createSignal, createMemo, createEffect, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { qty } from '../lib/format.js';
import GuildSearchInput from '../components/GuildSearchInput.jsx';
import ThemedTable, { GoldCell, UserCell } from '../components/ThemedTable/index.js';
import wm from '../lib/wmStore.js';

const GUILD_LABEL = { all: 'All', post: '⚔ Post', caravan: '🐎 Caravan', dealers: '💰 Dealers' };

export default function PlayerTracking(props = {}) {
  const [guild,       setGuild]       = createSignal(props.settings?.guild || 'all');
  const [days,        setDays]        = createSignal(Number(props.settings?.days) || 14);
  const [missingOnly, setMissingOnly] = createSignal(!!props.settings?.missingOnly);
  const [search,      setSearch]      = createSignal(props.settings?.search || '');
  const [searchD,     setSearchD]     = createSignal(props.settings?.search || '');
  const [customGuild, setCustomGuild] = createSignal(
    props.settings?.guildIndex ? { index: props.settings.guildIndex, name: props.settings.guildName || '' } : null
  );

  let _searchT = 0;
  onCleanup(() => clearTimeout(_searchT));
  function handleSearchInput(e) {
    const v = e.currentTarget.value;
    setSearch(v);
    clearTimeout(_searchT);
    _searchT = setTimeout(() => setSearchD(v), 250);
  }

  createEffect(() => {
    const wid = props.widget?.id;
    const g = guild();
    const d = days();
    const m = missingOnly();
    const s = searchD();
    const cg = customGuild();
    if (!wid) return;
    const cur = props.widget?.settings || {};
    const nextIdx = cg?.index || null;
    const nextName = cg?.name || '';
    if (
      (cur.guild || 'all') !== g ||
      (Number(cur.days) || 14) !== d ||
      !!cur.missingOnly !== m ||
      (cur.search || '') !== s ||
      (cur.guildIndex || null) !== nextIdx ||
      (cur.guildName || '') !== nextName
    ) {
      wm.setSettings(wid, {
        ...cur,
        guild: g,
        days: d,
        missingOnly: m,
        search: s,
        guildIndex: nextIdx,
        guildName: nextName,
      });
    }
  });

  const { data, loading, error, refetch } = useWidgetData('member-auditing',
    () => customGuild()
      ? { guildIndex: customGuild().index, days: days(), search: searchD() }
      : { guild: guild(), days: days(), missingOnly: missingOnly(), search: searchD() });

  const players    = createMemo(() => data()?.players || []);
  const isCustom   = createMemo(() => !!data()?.custom);
  const customMeta = createMemo(() => data()?.custom || null);

  function guildBadge(key, status) {
    if (!status) return null;
    const color = status.duesMet ? 'var(--t-green, #4ade80)' : 'var(--bad, #f87171)';
    const label = { post: 'P', caravan: 'C', dealers: 'D' }[key] || key[0].toUpperCase();
    return { label, color, title: `${key}: ${status.duesMet ? 'dues met' : 'MISSING DUES'} · ${qty(status.saleCount)} sales` };
  }

  function daysSilentCell(p) {
    const d = p.daysSinceActivity;
    if (d == null) return <span class="v2-tt-dim">—</span>;
    const colour = d > 14 ? 'var(--bad, #f87171)' : 'var(--t-green, #4ade80)';
    return <span class="v2-tt-mono" style={{ color: colour, 'font-weight': '600' }}>{d}d</span>;
  }

  const columns = createMemo(() => {
    const cols = [
      {
        key: 'sellerName',
        label: 'Player',
        sortable: true,
        type: 'string',
        canHide: false,
        render: (_v, row) => <UserCell name={row.sellerName} />,
      },
    ];

    if (!isCustom()) {
      cols.push({
        key: 'guilds',
        label: 'Guilds',
        sortable: false,
        render: (_v, row) => (
          <div class="v2-pt-badges" style={{ display: 'inline-flex', gap: '4px' }}>
            <For each={Object.entries(row.guildStatus || {})}>{([k, status]) => {
              const b = guildBadge(k, status);
              return b ? (
                <span
                  class="v2-pt-badge"
                  style={{
                    color: b.color,
                    'font-weight': '700',
                    padding: '1px 5px',
                    'border-radius': '4px',
                    background: 'rgba(255,255,255,0.06)',
                    border: `1px solid ${b.color}33`,
                  }}
                  title={b.title}
                >{b.label}</span>
              ) : null;
            }}</For>
          </div>
        ),
      });
    }

    cols.push(
      {
        key: 'totalSales',
        label: 'Sales',
        sortable: true,
        type: 'number',
        align: 'right',
        getValue: r => r.totalSales || 0,
        render: (v) => <span class="v2-tt-mono">{qty(v)}</span>,
      },
      {
        key: 'totalGold',
        label: 'Gold',
        sortable: true,
        type: 'currency',
        align: 'right',
        getValue: r => r.totalGold || 0,
        render: (v) => <GoldCell value={v} />,
      },
      {
        key: 'daysSinceActivity',
        label: 'Days silent',
        sortable: true,
        type: 'number',
        align: 'right',
        getValue: r => r.daysSinceActivity ?? 9999,
        render: (_v, row) => daysSilentCell(row),
      }
    );

    return cols;
  });

  return (
    <div class="v2-widget">
      <div class="v2-widget-hdr">
        <div class="v2-pt-controls">
          <For each={['all','post','caravan','dealers']}>{(k) => (
            <button
              type="button"
              class={`v2-gt-chip ${!customGuild() && guild() === k ? 'is-on' : ''}`}
              onClick={() => { setGuild(k); setCustomGuild(null); }}
            >{GUILD_LABEL[k]}</button>
          )}</For>
          <select
            class="v2-pt-select"
            value={days()}
            onChange={e => setDays(Number(e.currentTarget.value))}
          >
            <option value="7">7d</option>
            <option value="14">14d</option>
            <option value="30">30d</option>
            <option value="60">60d</option>
            <option value="90">90d</option>
          </select>
          <Show when={!customGuild()}>
            <label class="v2-pt-toggle">
              <input
                type="checkbox"
                checked={missingOnly()}
                onChange={e => setMissingOnly(e.currentTarget.checked)}
              />
              Missing dues only
            </label>
          </Show>
        </div>
        <button
          type="button"
          class="v2-widget-hdr__btn"
          style={{ 'margin-left': 'auto' }}
          title="Refresh"
          onClick={() => refetch()}
        >↻</button>
      </div>

      <div class="v2-gt-search">
        <GuildSearchInput
          placeholder="Look up players in any guild…"
          initialValue={customGuild()?.name || ''}
          onSelect={(g) => setCustomGuild(g)}
          onClear={() => setCustomGuild(null)}
        />
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading players…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <ThemedTable
            columns={columns()}
            rows={players()}
            storageKey="player_tracking"
            defaultSortKey="totalGold"
            defaultSortDir="desc"
            searchPlaceholder="Search player…"
            emptyText="No players found."
            rowClass={(row) => row.missingDues ? 'v2-gt-row--missing' : ''}
            extraControls={
              <Show when={isCustom() && customMeta()}>
                <div class="v2-tt-meta-badge">
                  {customMeta().guildName}
                  <Show when={!customMeta().isRedfur}>
                    <span class="v2-tt-dim" style={{ 'margin-left': '4px' }}>(non-Redfur)</span>
                  </Show>
                </div>
              </Show>
            }
          />
        </Show>
      </Show>
    </div>
  );
}
